<script setup lang="ts">
import useSeoTags from '@/composables/use-seo-tags';
import useSitecoreStore from '@/stores/sitecore';
import { storeToRefs } from 'pinia';
import { watch } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';
import AppLayout from './AppLayout.vue';
import RouteError from './RouteError.vue';
import RouteLoading from './RouteLoading.vue';

// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static routing isn't enough -
// we need to load dynamic route data from Sitecore when the client side route changes.
// So vue-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.
const props = defineProps<{
    route: RouteLocationNormalized;
}>();
const sitecoreStore = useSitecoreStore();
const { appContext, routing, pageEditing } = storeToRefs(sitecoreStore);

// if the app state has route data, we don't need to load it and don't need a loading screen
if (routing.value.data) {
    routing.value.loading = false;
}

// route path from vue router - if route was resolved, it's not a 404
if (props.route !== null) {
    routing.value.error = false;
}

// if we have an initial SSR state, and that state doesn't have a valid route data,
// then this is a 404 route.
if (!routing.value.data) {
    routing.value.error = true;
}

const updateRouteData = () => {
    let itemPath = Array.isArray(props.route.params.sitecoreRoute)
        ? props.route.params.sitecoreRoute.join('/')
        : appContext.value.startPath;

    if (!itemPath.startsWith('/')) {
        itemPath = `/${itemPath}`;
    }

    itemPath = `${appContext.value.sitecoreBasePath}${itemPath}`;

    sitecoreStore.getSitecoreData(itemPath);
};

watch(
    () => props.route,
    (newRoute, oldRoute) => {
        if (newRoute.hash !== '' && newRoute.path === oldRoute.path) {
            return;
        }
        // If in Sitecore editor - force reload instead of route data update
        // Avoids confusing Sitecore's editing JS
        if (pageEditing.value) {
            window.location.assign(newRoute.path);
            return;
        }

        updateRouteData();
    }
);

// If no existing route data is present (from SSR), get Layout Service fetching the route data
if (!routing.value.data) {
    updateRouteData();
}

useSeoTags();
</script>

<template>
    <RouteLoading v-if="routing.loading" />
    <template v-else>
        <RouteError v-if="routing.error || !routing.data" />
        <AppLayout
            v-else
            :route="routing.data"
        />
    </template>
</template>
